import React from 'react';
import Balancer from 'react-wrap-balancer';

import styled from 'styled-components';

import ScrollReveal from '@components/common/ScrollReveal';
import { Container } from '@components/parts/Container';

import { UploadedImage } from '@customTypes/UploadedImage';

type Props = {
  titleHint: string;
  title: string;
  coverages: {
    group: string;
    regions: string;
  }[];
  rightImage: UploadedImage;
};

const RegionIcon = () => (
  <svg className="icon" width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="15" cy="15" r="15" fill="#EFF7FF" />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M14.9872 7.5L15 7.50008L15.0128 7.5C15.0282 7.49998 15.0484 7.50007 15.0731 7.50047C15.1226 7.50125 15.1905 7.50323 15.2742 7.50782C15.4414 7.51698 15.6738 7.53665 15.9511 7.57856C16.5016 7.66176 17.2516 7.83593 18.026 8.20556C19.6248 8.96875 21.2535 10.542 21.4863 13.5596L21.4875 13.5726C21.4888 13.5889 21.4904 13.6096 21.492 13.6348C21.4951 13.685 21.4982 13.7528 21.4995 13.8366C21.5019 14.004 21.4969 14.2359 21.4697 14.5197C21.4153 15.087 21.2719 15.8654 20.9198 16.7495C20.2077 18.5375 18.6715 20.6828 15.469 22.3832L15 22.6322L14.531 22.3832C11.3285 20.6828 9.79226 18.5375 9.08021 16.7495C8.72812 15.8654 8.58473 15.087 8.53034 14.5197C8.50314 14.2359 8.49811 14.004 8.50054 13.8366C8.50176 13.7528 8.50485 13.685 8.508 13.6347C8.50957 13.6096 8.51116 13.5888 8.51254 13.5726L8.51372 13.5596C8.74654 10.542 10.3752 8.96875 11.974 8.20556C12.7484 7.83593 13.4984 7.66176 14.0489 7.57856C14.3262 7.53665 14.5586 7.51698 14.7258 7.50782C14.8095 7.50323 14.8774 7.50125 14.9269 7.50047C14.9516 7.50007 14.9718 7.49998 14.9872 7.5ZM16.5 13.5C16.5 14.3284 15.8284 15 15 15C14.1716 15 13.5 14.3284 13.5 13.5C13.5 12.6716 14.1716 12 15 12C15.8284 12 16.5 12.6716 16.5 13.5Z"
      fill="#055CF5"
    />
  </svg>
);

const Wrapper = styled.div`
  padding-top: 90px;
  padding-bottom: 90px;
  @media (min-width: 992px) {
    padding-bottom: 160px;
  }
  .region-info {
    display: flex;
    flex-direction: column;
    margin-top: 40px;
    @media (min-width: 992px) {
      margin-top: 70px;
    }
    .group {
      margin-bottom: 1em;
      font-size: 1.1em;
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      .name {
        .icon {
          margin-right: 0.4em;
        }
        display: flex;
        align-items: center;
        font-weight: 700;
        margin-right: 2em;
      }
      .regions {
        margin-top: 3px;
        @media (min-width: 992px) {
          margin-top: 1px;
        }
      }
    }
  }
  div.up {
    display: flex;
    flex-direction: column;
    .region-info {
      display: none;
    }
    position: relative;
    div.image-container {
      flex: 1;
      text-align: right;

      img {
        padding-top: 50px;
        padding-left: 0;
        max-width: 100%;
        min-width: 30vw;
      }
    }
  }
  @media (min-width: 992px) {
    div.up {
      flex-direction: row;
      .link {
        display: flex;
      }
      div.image-container {
        img {
          margin-bottom: 160px;
          padding-top: 0;
          padding-left: 100px;
        }
      }
      .region-info {
        display: flex;
      }
    }
    padding-top: 160px;
    div.down {
      .region-info {
        display: none;
      }
    }
  }
  h1,
  h2,
  h3 {
    font-weight: 700;
    white-space: pre;
  }
  .title-hint {
    margin-bottom: 12px;
    font-size: 16px;
    font-weight: 500;
    opacity: 0.8;
    @media (min-width: 992px) {
      font-size: 24px;
    }
  }
`;

export const ServiceCoverage: React.FC<Props> = (props) => {
  const {
    // prettier-ignore
    titleHint,
    title,
    rightImage,
    coverages,
  } = props;

  const regionInfo = (
    <div className="region-info">
      {coverages?.map(({ group, regions }) => (
        <div className="group" key={group}>
          <div className="name">
            <RegionIcon />
            {group}
          </div>
          <div className="regions">{regions}</div>
        </div>
      ))}
    </div>
  );

  return (
    <Container>
      <Wrapper>
        <div className="up">
          <ScrollReveal>
            <div className="title-hint">{titleHint}</div>
            <h3>
              <Balancer>{title}</Balancer>
            </h3>
            {regionInfo}
          </ScrollReveal>
          {rightImage?.data?.attributes.url ? (
            <ScrollReveal intervalDelay={500} className="image-container">
              <img src={rightImage.data.attributes.url} alt={title} />
            </ScrollReveal>
          ) : null}
        </div>
        <ScrollReveal className="down">{regionInfo}</ScrollReveal>
      </Wrapper>
    </Container>
  );
};
